<template>
  <Layout
    max-width="100%"
    is-card
    v-bind="$attrs"
  >
    <template #message>
      <slot name="message" />
    </template>

    <template #helper>
      <ProposalFilters
        :product-id="productId"
        @message="setMessage"
        @paginate="paginate"
      />
    </template>

    <div class="proposal-view">
      <ProposalFiltersBy
        class="mb-3"
        @paginate="paginate"
      >
        <template #actions>
          <section class="is-flex is-align-items-center">
            <button
              v-if="selectRowOptions.selected.length"
              class="button is-warning"
              @click.prevent="open({ modal: 'massStatusAlteration' })"
            >
              <span>
                Alterar {{ selectRowOptions.selected.length }} status
              </span>
            </button>
            <button
              v-if="proposal.list?.length"
              class="button is-success ml-2"
              @click="downloadCSV"
            >
              <ExportIcon :class="!isMobile ? 'mr-2' : ''" />
              {{ !isMobile ? 'Exportar CSV' : '' }}
            </button>
            <button
              v-if="isProductFGTS && isCpfFilterShaped && canSeeConsolidatedPayoffButton"
              class="button is-link ml-2"
              @click="requestConsolidatedPayoff"
            >
              <span>Gerar quitação consolidada</span>
            </button>
          </section>
        </template>
      </ProposalFiltersBy>

      <BaseTable
        has-dbl-click
        :selectable="canDoMassAlteration"
        selectable_by="proposal_id"
        :headers="[
          { label: 'Cliente', name: 'client', show: !isMobile },
          { label: 'CPF', name: 'document', show: isMobile },
          { label: 'Nome', name: 'name', show: isMobile },
          {
            label: 'Status',
            name: 'status',
            align: 'center',
          },
          {
            label: isTypist ? 'Promotora' : 'Responsável',
            name: 'responsible',
            show: !isCreditor,
          },
          { label: 'Criado em', name: 'created_at' },
          { label: 'Valor liberado', name: 'net_amount' },
          {
            label: 'Banco',
            name: 'supplier_name',
            show: isProductFGTS && isInternalUser,
          },
          { label: 'CCB', name: 'CCB' },
          { label: 'Desembolsado Em', name: 'loaned_at' },
        ]"
        :actions="[
          {
            label: 'Visualizar simulação',
            show: !isCreditor,
            icon: () => import('@/assets/icons/ProposalDetail.vue'), // fas fa-search
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'simulation', data: proposal }),
          },
          {
            label: 'Ver histórico',
            show: !isCreditor,
            icon: () => import('@/assets/icons/ProposalHistory.vue'),
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'history', data: proposal }),
          },
          {
            label: 'Comprovação por foto',
            show: (proposal) =>
              proposal.token && (proposal.status !== 'Paga' || isInternalUser),
            icon: () => import('@/assets/icons/ProposalAntifraud.vue'),
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'antifraud', data: proposal }),
          },
          {
            label: 'Editar dados do usuário',
            icon: () => import('@/assets/icons/ProposalEdit.vue'),
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'edit', data: proposal }),
          },
          {
            label: 'Atualizar proposta',
            show: (proposal) => !isPromoterBlocked
              && isProductFGTS
              && canUpdateProposalStatus(proposal.status)
              && isTodayPastCreated(proposal.created_at),
            icon: () => import('@/assets/icons/Sync.vue'),
            iconClass: 'not-stroke',
            variant: 'success',
            action: (proposal) => open({ modal: 'resimulate', data: proposal }),
          },
          {
            label: 'Conferir estornos do cartão',
            show: isProductCredCard && canManageCardRefunds,
            icon: () => import('@/assets/icons/ProposalCardRefund.vue'),
            iconClass: 'not-stroke',
            action: (proposal) => open({ modal: 'creditCardRefunds', data: proposal }),
          },
        ]"
        :data="proposal.list"
        @selection="(rows) => selectRowOptions.selected = rows"
        @dblClickAct="detailsProposals"
      >
        <!-- Client -->
        <template #client="{ row }">
          <CopyToClipboard class="is-highlighted">
            <span class="is-nowrap is-bold">
              {{ row.document }}
            </span>
          </CopyToClipboard>
          <p>
            {{ row?.full_name || row?.name }}
          </p>
        </template>

        <!-- Status -->
        <template #status="{ row: proposal }">
          <span
            v-tooltip="proposal.obs ?? null"
            class="tag"
            :has-action="!!proposal.issue_type
              && hasValidStatus(proposal.status)
              && !isProductCredCard
            "
            :class="proposalStatusCypher[proposal?.status]?.tag"
            @click="!!proposal.issue_type
              && !isProductCredCard
              && hasValidStatus(proposal.status)
              && open({ modal: 'issue', data: proposal })
            "
          >
            {{ proposalStatusCypher[proposal?.status]?.translation || proposal.status }}
          </span>
        </template>

        <!-- Created_at -->
        <template #created_at="{ value }">
          <span class="is-nowrap">{{ omitYear(value) }}</span>
        </template>

        <!-- Responsible -->
        <template #responsible="{ row }">
          <span
            v-tooltip="getSubAndResponsible(row)"
            class="ellipsis"
          >
            {{ getResponsibleOrPromoterData(row) }}
          </span>
        </template>

        <!-- Net Amount -->
        <template #net_amount="{ value }">
          {{ (value || 0) | money }}
        </template>

        <!-- CCB -->
        <template #CCB="{ row: proposal }">
          <template v-for="(id, idIndex) in split(proposal.external_proposal_number, ',')">
            <template v-if="id">
              <button
                v-if="hasCCBCreated(proposal)"
                :key="`btn-${idIndex}`"
                class="button is-link px-0"
                @click="open({ modal: 'status', data: proposal })"
              >
                {{ id }}
              </button>
              <span
                v-else
                :key="`span-${idIndex}`"
                class="is-ghost px-0 is-nowrap"
              >
                {{ id }}
              </span>
            </template>
          </template>
        </template>

        <!-- Loaned at -->
        <template #loaned_at="{ value }">
          {{ value || '-' }}
        </template>

        <template
          v-if="listingSearch.cancelled || listingSearch.awaitingSearch"
          #no-data
        >
          <ListingSearchEmptyState />
        </template>
      </BaseTable>

      <div class="proposal-view__footer">
        <ThePagination
          :data="pagination.data"
          @paginate="paginate"
        />
      </div>
    </div>

    <!-- check history -->
    <ModalHistory
      v-if="modal.history"
      @close="close({ key: 'history' })"
    />

    <!-- double click modal -->
    <!-- register status and start proposal -->
    <ModalProposals
      v-if="modal.proposal"
      :statuses="statuses"
      @close="close({ key: 'proposal', paginate: true })"
    />

    <!-- Proposal status -->
    <ProposalFlow
      v-if="modal.status"
      flow="status"
      @close="close({ key: 'status' })"
      @proposalChanged="close({ key: 'status', paginate: true })"
      @receipt="($event) => {
        receiptData = { external_proposal_id: $event.externalId };
        close({ key: 'status' });
        open({ modal: 'receipt' });
      }"
      @proposalWithdrawal="($event) => {
        withdrawalDetails = $event
        close({ key: 'status' });
        open({ modal: 'withdrawal' });
      }"
      @proposalAcquittance="($event) => {
        proposal.external_proposal_number = $event.external_proposal_number;
        close({ key: 'status' });
        open({ modal: 'acquittance' });
      }"
      @creditCardTransactionData="() => {
        close({ key: 'status' });
        open({ modal: 'creditCardTransactionData' });
      }"
    />

    <!-- open from status -->
    <ModalProposalStatusReceipt
      v-if="modal.receipt && isSupplierQiTech"
      :receipt-data="receiptData"
      @close="close({ key: 'receipt' })"
      @back="() => {
        close({ key: 'receipt' });
        open({ modal: 'status' });
      }"
    />
    <ModalProposalStatusReceiptBMP
      v-if="modal.receipt && isSupplierBMP"
      :receipt-data="receiptData"
      :details="proposal.details"
      @close="close({ key: 'receipt' })"
      @back="() => {
        close({ key: 'receipt' });
        open({ modal: 'status' });
      }"
    />
    <ModalProposalStatusReceiptBMS
      v-if="modal.receipt && isSupplierBMS"
      :receipt-data="receiptData"
      :details="proposal.details"
      @close="close({ key: 'receipt' })"
      @back="() => {
        close({ key: 'receipt' });
        open({ modal: 'status' });
      }"
    />
    <ModalProposalWithdrawal
      v-if="modal.withdrawal"
      :supplier-id="stateProposal.supplier_id"
      :details="withdrawalDetails"
      @close="close({ key: 'withdrawal' })"
      @back="() => {
        close({ key: 'withdrawal' });
        open({ modal: 'status' });
      }"
    />
    <ProposalFlow
      v-if="modal.acquittance"
      flow="acquittance"
      @back="() => {
        close({ key: 'acquittance' });
        open({ modal: 'status' });
      }"
    />

    <ModalCreditCardTransactionData
      v-if="modal.creditCardTransactionData"
      @close="close({ key: 'creditCardTransactionData' })"
      @back="() => {
        close({ key: 'creditCardTransactionData' });
        open({ modal: 'status' });
      }"
    />

    <ModalCreditCardRefunds
      v-if="modal.creditCardRefunds"
      @close="close({ key: 'creditCardRefunds' })"
    />

    <!-- edit all the given user info -->
    <ModalData
      v-if="modal.edit"
      :product-id="productId"
      :steps-list="steps"
      @close="close({ key: 'edit' })"
    />

    <!-- antifraud -->
    <AntifraudModal
      v-if="modal.antifraud"
      @close="close({ key: 'antifraud', paginate: true })"
    />

    <!-- issues -->
    <ModalIssue
      v-if="modal.issue"
      :product-id="productId"
      @close="close({ key: 'issue' })"
    />

    <!-- simulation (validation) -->
    <ModalSimulationResponse
      v-if="modal.simulation"
      :product-id="productId"
      @close="close({ key: 'simulation' })"
    />

    <!-- mass status change -->
    <BaseSimpleModal
      v-if="modal.massStatusAlteration"
      has-close-button
      width="600px"
      @close="close({ key: 'massStatusAlteration' })"
    >
      <template #title>
        Alterar status em massa
      </template>
      <template #body>
        <base-select
          v-model="form.status"
          selected=""
          :options="statuses"
          placeholder="Selecione o status"
        >
          <span>Qual status você deseja atribuir?</span>
        </base-select>
      </template>
      <template #actions>
        <div class="buttons ml-atuo">
          <button
            class="button"
            @click="close({ key: 'massStatusAlteration' })"
          >
            Cancelar
          </button>
          <button
            class="button is-primary"
            @click="confirmMassAlteration"
          >
            Alterar Status
          </button>
        </div>
      </template>
    </BaseSimpleModal>

    <BaseSimpleModal
      v-if="modal.consolidatedPayoff"
      has-close-button
      width="700px"
      @close="closeConsolidatedPayoff"
    >
      <template #title>
        Quitação consolidada FGTS
      </template>
      <template #body>
        <template v-if="consolidatedPayoffStep === 'setup'">
          <p class="mb-4">
            Defina a data de vencimento. Ela será usada na prévia e na emissão de cada contrato.
          </p>
          <BaseCalendar
            v-model="consolidatedPayoffDate"
            :limit-date="false"
            :is-range="false"
            detached
            value-type="YYYY-MM-DD"
            format="DD/MM/YYYY"
          >
            Data de vencimento
          </BaseCalendar>
          <p v-if="isConsolidatedPayoffDateToday" class="notification is-warning is-light py-2 px-3 mt-3 mb-0">
            Contratos BMP exigem vencimento em data futura e retornarão erro
            na prévia se a data for hoje. Os demais contratos não são afetados.
          </p>
        </template>

        <template v-else-if="consolidatedPayoffStep === 'preview'">
          <p class="mb-3">
            CPF {{ consolidatedPayoffDocument }} — vencimento {{ consolidatedPayoffDate }}
          </p>
          <p v-if="consolidatedPayoff.error" class="has-text-danger">
            {{ consolidatedPayoff.error }}
          </p>
          <table v-else class="table is-fullwidth" aria-label="Prévia da quitação consolidada">
            <caption class="is-sr-only">
              Prévia da quitação consolidada
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  Proposta
                </th>
                <th scope="col">
                  Bancarizadora
                </th>
                <th scope="col">
                  Parcelas
                </th>
                <th scope="col">
                  Valor original
                </th>
                <th scope="col">
                  Valor para vencimento
                </th>
                <th scope="col">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in consolidatedPayoff.items" :key="item.proposal_id">
                <td>{{ item.proposal_id }}</td>
                <td>{{ consolidatedPayoffSupplierName(item.supplier_id) }}</td>
                <td>{{ item.installments?.length || '-' }}</td>
                <td>{{ item.original_value === null ? '-' : formatMoney(item.original_value) }}</td>
                <td>{{ item.total_value === null ? '-' : formatMoney(item.total_value) }}</td>
                <td>{{ consolidatedPayoffItemStatusLabel(item) }}</td>
              </tr>
            </tbody>
          </table>
        </template>

        <template v-else>
          <template v-if="!consolidatedPayoff.id">
            <p>{{ consolidatedPayoff.error || 'Gerando quitação consolidada...' }}</p>
          </template>
          <template v-else>
            <p class="mb-3">
              CPF {{ consolidatedPayoff.document }} — status geral:
              <strong>{{ consolidatedPayoffStatusLabel }}</strong>
            </p>

            <table class="table is-fullwidth" aria-label="Contratos da quitação consolidada">
              <caption class="is-sr-only">
                Contratos da quitação consolidada
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    Proposta
                  </th>
                  <th scope="col">
                    Bancarizadora
                  </th>
                  <th scope="col">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in consolidatedPayoff.items" :key="item.proposal_id">
                  <td>{{ item.proposal_id }}</td>
                  <td>{{ consolidatedPayoffSupplierName(item.supplier_id) }}</td>
                  <td>{{ consolidatedPayoffItemStatusLabel(item) }}</td>
                </tr>
              </tbody>
            </table>

            <p v-if="consolidatedPayoff.pdf_url" class="mt-3">
              <a :href="consolidatedPayoff.pdf_url" target="_blank" rel="noopener">Baixar PDF consolidado</a>
            </p>
          </template>
        </template>
      </template>
      <template #actions>
        <div class="buttons ml-auto">
          <button class="button" @click="closeConsolidatedPayoff">
            Cancelar
          </button>
          <button
            v-if="consolidatedPayoffStep === 'setup'"
            class="button is-primary"
            :class="{ 'is-loading': consolidatedPayoffLoading }"
            :disabled="!consolidatedPayoffDate || consolidatedPayoffLoading"
            @click="previewConsolidatedPayoff"
          >
            Consultar prévia
          </button>
          <button
            v-else-if="consolidatedPayoffStep === 'preview'"
            class="button is-primary"
            :class="{ 'is-loading': consolidatedPayoffLoading }"
            :disabled="!hasConsolidatedPayoffReadyItem || consolidatedPayoffLoading"
            @click="confirmConsolidatedPayoff"
          >
            Confirmar emissão
          </button>
          <button v-else class="button is-primary" @click="closeConsolidatedPayoff">
            Fechar
          </button>
        </div>
      </template>
    </BaseSimpleModal>

    <!-- fgts re-simulation confirm -->
    <BaseSimpleModal
      v-if="modal.resimulate"
      has-close-button
      width="600px"
      @close="close({ key: 'resimulate' })"
    >
      <template #title>
        Simular Proposta FGTS
      </template>
      <template #body>
        <p>
          Essa ação irá atualizar a proposta em questão.
          É necessário fazer isso, no caso da proposta
          <strong>não ter sido paga no dia de digitação da mesma</strong>.
        </p>
        <p class="mt-3">
          Deseja prosseguir?
        </p>
      </template>
      <template #actions>
        <div class="buttons ml-auto">
          <button
            class="button"
            @click="close({ key: 'resimulate' })"
          >
            Cancelar
          </button>
          <button
            class="button is-primary"
            @click="reSimulateProposalFGTS"
          >
            Simular novamente
          </button>
        </div>
      </template>
    </BaseSimpleModal>

    <!-- others modals -->
    <slot />
  </Layout>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { toast } from '@/utils/message';
import { reactive } from 'vue';

import {
  accountsAPI,
  fgtsQiTechAPI,
  payoffAPI,
  proposalsAPI,
} from '@/services';

import config from '@/data/config';
import filterStorageMixin from '@/mixins/filterStorage.mixin';
import loadingMixin from '@/mixins/loadingView.mixin';
import roleManagerMixin from '@/mixins/roleManager.mixin';
import sanitizeHtmlMixin from '@/mixins/sanitizeHtml.mixin';

import Layout from '@/layouts/Layout.vue';
import ThePagination from '@/components/ThePagination.vue';
import ProposalFilters from '@/components/ProposalFilters.vue';
import ProposalFiltersBy from '@/components/ProposalFiltersBy.vue';
import ListingSearchEmptyState from '@/components/ListingSearchEmptyState.vue';

import { cypher as proposalStatusCypher } from '@/data/proposalStatusCypher.js';

import useProposalSupplier from '@/composables/useProposalSupplier';
import useProposalListActions from '@/composables/useProposalListActions';
import useListingSearch from '@/composables/useListingSearch';

export default {
  name: 'ProposalsList',
  components: {
    Layout,
    ThePagination,
    ProposalFilters,
    ProposalFiltersBy,
    ListingSearchEmptyState,

    // async
    ExportIcon: () => import('@/assets/icons/ExportIcon.vue'),
    CopyToClipboard: () => import('@/components/CopyToClipboard.vue'),
    BaseSelect: () => import('@/components/Base/Select/index.vue'),
    BaseCalendar: () => import('@/components/BaseCalendar.vue'),
    BaseSimpleModal: () => import('@/components/BaseSimpleModal.vue'),
    BaseTable: () => import('@/components/Base/Table/index.vue'),

    // modals
    ModalProposals: () => import('@/components/ModalProposals.vue'),
    ModalHistory: () => import('@/components/ModalHistory.vue'),
    ModalData: () => import('@/components/ModalData/index.vue'),
    ModalIssue: () => import('@/components/ModalIssue.vue'),
    ModalSimulationResponse: () => import('@/components/ModalSimulationResponse.vue'),
    AntifraudModal: () => import('@/components/AntifraudModal/Index.vue'),

    // ccb
    ProposalFlow: () => import('@/components/proposal/ProposalFlow.vue'),

    // receipt
    ModalProposalStatusReceipt: () => import('@/components/ModalProposalStatusReceipt.vue'),
    ModalProposalStatusReceiptBMP: () => import('@/components/ModalProposalStatusReceiptBMP.vue'),
    ModalProposalStatusReceiptBMS: () => import('@/components/ModalProposalStatusReceiptBMS.vue'),

    // acquittance
    ModalProposalWithdrawal: () => import('@/components/ModalProposalWithdrawal.vue'),

    // credit card
    ModalCreditCardTransactionData: () => import('@/components/ModalCreditCardTransactionData.vue'),
    ModalCreditCardRefunds: () => import('@/components/ModalCreditCardRefunds.vue'),
  },
  mixins: [
    filterStorageMixin,
    loadingMixin,
    roleManagerMixin,
    sanitizeHtmlMixin,
  ],
  props: {
    productId: {
      type: [Number, String],
      required: true,
    },
    statuses: {
      type: Array,
      required: true,
    },
    steps: {
      type: Set,
      required: true,
    },
  },
  setup() {
    const modal = reactive({
      proposal: false,
      history: false,
      status: false,
      receipt: false,
      edit: false,
      antifraud: false,
      withdrawal: false,
      acquittance: false,
      creditCardTransactionData: false,
      creditCardRefunds: false,
      issue: false,
      simulation: false,
      massStatusAlteration: false,
      resimulate: false,
      consolidatedPayoff: false,
    });

    return {
      modal,
      listingSearch: useListingSearch(),
      ...useProposalSupplier(),
      ...useProposalListActions({ modal }),
    };
  },
  data: () => ({
    withdrawalDetails: null,
    receiptData: false,
    form: {
      status: '',
    },
    proposalStatusCypher,
    consolidatedPayoff: {},
    consolidatedPayoffDate: '',
    consolidatedPayoffDocument: '',
    consolidatedPayoffLoading: false,
    consolidatedPayoffStep: 'setup',
    consolidatedPayoffTimer: null,
  }),
  computed: {
    ...mapState({
      stateProposal: (state) => state.proposal.proposal,
    }),
    ...mapGetters({
      isPromoterBlocked: 'user/isPromoterBlocked',
      isMobile: 'app/isMobile',
      filters: 'proposal/validFilters',
      isInternalUser: 'user/isInternalUser',
    }),
    isProductFGTS() {
      return this.productId === config.PRODUCT_ID_FGTS;
    },
    isProductCredCard() {
      return this.productId === config.PRODUCT_ID_CREDIT_CARD;
    },
    // Same roles as GET/POST v1/cards/refunds, which also require internal_employee.
    canManageCardRefunds() {
      return this.isInternalUser && [
        this.isSuper,
        this.isAdmin,
        this.isAntifraud,
        this.isCompliance,
        this.isCommercial,
      ].some((role) => role);
    },
    canDoMassAlteration() {
      return [
        this.isAntifraud,
        this.isAdmin,
        this.isSuper,
      ].some((role) => role);
    },
    canEditObs() {
      return [
        this.isAdmin,
        this.isSuper,
        this.isManager,
        this.isAntifraud,
        this.isCommercial,
        this.isCompliance,
        this.isMasterOrFinancial,
      ].some((isValid) => isValid);
    },
    isCpfFilterShaped() {
      return `${this.filters.multi_search || ''}`.replace(/\D/g, '').length === 11;
    },
    // Consolidated payoff under prod validation — button restricted to
    // user id 4933473 (diego.araujo@pratadigital.com.br) until cleared for everyone.
    // Remove this gate once the feature is validated with real data.
    canSeeConsolidatedPayoffButton() {
      return this.$store.state.user.userInfo?.id === 4933473;
    },
    consolidatedPayoffStatusLabel() {
      return {
        processing: 'Processando',
        completed: 'Concluído',
        completed_partial: 'Concluído parcialmente',
        failed: 'Falhou',
      }[this.consolidatedPayoff.status] || this.consolidatedPayoff.status;
    },
    hasConsolidatedPayoffReadyItem() {
      return this.consolidatedPayoff.items?.some((item) => item.status === 'ready');
    },
    isConsolidatedPayoffDateToday() {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      return this.consolidatedPayoffDate === todayStr;
    },
  },
  watch: {
    'modal.massStatusAlteration': {
      handler(status) {
        if (status === true) return;
        this.selectRowOptions.selectAll = false;
        this.selectRowOptions.selected = [];
      },
    },
    loading: {
      handler(status) {
        if (status === true) this.setMessage('');
      },
    },
  },
  mounted() {
    this.$nextTick(() => this.listingSearch.arrive(() => this.close({ key: 'all', paginate: true })));
  },
  beforeDestroy() {
    clearInterval(this.consolidatedPayoffTimer);
  },
  methods: {
    ...mapActions({
      setProposalData: 'proposal/SET_PROPOSAL_DATA',
    }),
    canUpdateProposalStatus(proposalStatus) {
      return [
        'Em análise',
        'Pendência',
        'Aguardando videoconferência',
      ].includes(proposalStatus);
    },

    // handlers
    split(arr, data) {
      return String(arr || '').replace(/\/[A-Za-z0-9]+$/, '').split(data);
    },
    getResponsibleOrPromoterData({ promoter_name, responsible_name }) {
      return this.isTypist ? promoter_name : responsible_name;
    },
    async downloadCSV() {
      const product_id = this.productId;

      this.loading = true;

      await proposalsAPI
        .downloadCSV({ ...this.filters, product_id, output: 'csv' })
        .then((data) => {
          const blob = new Blob([`\ufeff${data}`], { type: 'text/csv;charset=utf-8' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.setAttribute('download', 'propostas.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
        });
    },
    hasValidStatus(status) {
      return [
        'Em análise',
        'Pendência',
        'Aguardando consulta',
        'Aguardando pagamento',
        'Aguardando relançamento',
        'Aguardando desaverbação',
      ].some((option) => option === status);
    },
    isTodayPastCreated(date) {
      let [splittedDate] = date.split(' ');
      splittedDate = splittedDate.split('/');

      if (splittedDate.length === 2) splittedDate.push('2024');

      const [day, month, year] = splittedDate;

      const created = new Date(year, month - 1, day);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      created.setHours(0, 0, 0, 0);

      return today.getTime() > created.getTime();
    },

    // api
    async paginate({ page = 1 } = {}) {
      const signal = this.listingSearch.start();
      this.loading = true;

      await proposalsAPI
        .getProposals({ page, ...this.filters, product_id: this.productId }, { signal })
        .then(({ data }) => {
          this.$set(this.pagination, 'data', data);
          this.$set(this.proposal, 'list', data.data);
        })
        .catch((err) => {
          if (!this.listingSearch.isCancel(err)) return this.handlerError(err);
          if (!this.listingSearch.cancelled) return undefined;
          this.$set(this.pagination, 'data', {});
          return this.$set(this.proposal, 'list', []);
        })
        .finally(() => {
          if (this.listingSearch.finish(signal)) this.loading = false;
        });
    },
    async detailsProposals({ event, ...proposal }) {
      const invalidNodes = ['button', 'input'];

      if (!this.canEditObs) return;
      if (this.isCreditor || invalidNodes.includes(event.target.localName)) return;

      if (proposal) this.setProposalData(proposal);

      this.loading = true;

      try {
        await accountsAPI.getInfoUser();
        this.modal.proposal = true;
      } catch (error) {
        this.handlerError(error);
      } finally {
        this.loading = false;
      }
    },
    async confirmMassAlteration() {
      this.loading = true;

      await proposalsAPI
        .massStatusUpdate(this.selectRowOptions.selected, this.form.status)
        .then(() => {
          toast('Status atualizado com sucesso!', 'success');
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
          this.close({
            key: 'massStatusAlteration',
            paginate: true,
          });
        });
    },
    async reSimulateProposalFGTS() {
      this.loading = true;

      await fgtsQiTechAPI
        .reSimulateFGTS({ proposal_id: this.stateProposal.proposal_id })
        .then(({ data }) => {
          toast(data.data, 'success');
        })
        .catch(this.handlerError)
        .finally(() => {
          this.loading = false;
          this.close({
            key: 'resimulate',
            paginate: true,
          });
        });
    },
    requestConsolidatedPayoff() {
      this.consolidatedPayoff = {};
      this.consolidatedPayoffDocument = `${this.filters.multi_search || ''}`.replace(/\D/g, '');
      const today = new Date();
      this.consolidatedPayoffDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      this.consolidatedPayoffStep = 'setup';
      this.modal.consolidatedPayoff = true;
    },
    async previewConsolidatedPayoff() {
      this.consolidatedPayoffLoading = true;
      this.consolidatedPayoff = {};

      try {
        const { data } = await payoffAPI.previewConsolidated({
          document: this.consolidatedPayoffDocument,
          reference_date: this.consolidatedPayoffDate,
        });
        this.consolidatedPayoff = data.data;
        this.consolidatedPayoffStep = 'preview';
      } catch (error) {
        this.consolidatedPayoff = { error: 'Não foi possível consultar a prévia da quitação consolidada.' };
        this.consolidatedPayoffStep = 'preview';
        this.handlerError(error);
      } finally {
        this.consolidatedPayoffLoading = false;
      }
    },
    async confirmConsolidatedPayoff() {
      this.consolidatedPayoffLoading = true;

      try {
        const { data } = await payoffAPI.requestConsolidated({
          document: this.consolidatedPayoffDocument,
          reference_date: this.consolidatedPayoffDate,
        });
        this.consolidatedPayoff = data.data;
        this.consolidatedPayoffStep = 'progress';
        this.pollConsolidatedPayoffStatus();
      } catch (error) {
        this.consolidatedPayoff = { error: 'Não foi possível iniciar a quitação consolidada.' };
        this.consolidatedPayoffStep = 'progress';
        this.handlerError(error);
      } finally {
        this.consolidatedPayoffLoading = false;
      }
    },
    pollConsolidatedPayoffStatus() {
      clearInterval(this.consolidatedPayoffTimer);

      // Ceiling matches the backend job timeout (MAX_POLL_ATTEMPTS=5 x
      // RETRY_DELAY_MINUTES=2 ~ 10min until it closes as completed_partial/failed).
      const maxAttempts = 150; // 150 x 5s = 12.5min
      let attempts = 0;

      this.consolidatedPayoffTimer = setInterval(() => {
        attempts += 1;

        payoffAPI
          .getConsolidatedStatus(this.consolidatedPayoff.id)
          .then(({ data }) => {
            this.consolidatedPayoff = data.data;

            if (['completed', 'completed_partial', 'failed'].includes(data.data.status)) {
              clearInterval(this.consolidatedPayoffTimer);
            }
          })
          .catch(() => {
            clearInterval(this.consolidatedPayoffTimer);
          });

        if (attempts >= maxAttempts) {
          clearInterval(this.consolidatedPayoffTimer);
          if (!['completed', 'completed_partial', 'failed'].includes(this.consolidatedPayoff.status)) {
            this.consolidatedPayoff = { ...this.consolidatedPayoff, error: 'Tempo limite excedido aguardando o processamento. Consulte novamente mais tarde.' };
          }
        }
      }, 5000);
    },
    closeConsolidatedPayoff() {
      clearInterval(this.consolidatedPayoffTimer);
      this.modal.consolidatedPayoff = false;
      this.consolidatedPayoff = {};
      this.consolidatedPayoffDate = '';
      this.consolidatedPayoffDocument = '';
      this.consolidatedPayoffStep = 'setup';
      this.consolidatedPayoffLoading = false;
    },
    formatMoney(value) {
      return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    consolidatedPayoffSupplierName(supplierId) {
      return {
        [config.SUPPLIER_ID_QITECH]: 'QiTech',
        [config.SUPPLIER_ID_BMP]: 'BMP',
        [config.SUPPLIER_ID_BMS]: 'BMS',
        [config.SUPPLIER_ID_CARTOS]: 'Cartos',
        [config.SUPPLIER_ID_CELCOIN]: 'Celcoin',
      }[supplierId] || `Fornecedor ${supplierId}`;
    },
    consolidatedPayoffItemStatusLabel(item) {
      const label = {
        processing: 'Processando',
        generated: 'Gerado',
        pending: 'Pendente',
        error: 'Erro',
        unsupported: 'Não suportado',
      }[item.status] || item.status;

      const friendlyError = {
        'DtCalculo deve ser uma data futura.': 'vencimento precisa ser uma data futura para esta bancarizadora',
      }[item.error] || item.error;

      return friendlyError ? `${label} (${friendlyError})` : label;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';
@import '@/assets/scss/src/_buttonsVariables.scss';

.selected-operation-list {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.slide-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-leave-active {
  transition: all 0.2s ease-out;
}
.slide-enter-active {
  transition: all 0.2s ease;
}
.slide-enter {
  transform: translateX(-20px);
  opacity: 0;
}
.showActionButton {
  &button {
    text-decoration: none;
  }

  i {
    font-size: 1.5rem;
  }
  &.open {
    transform: rotate(-180deg);
    transition: transform .5s;
  }
  &.close {
    transform: rotate(0deg);
    transition: transform .5s;
  }
}
.proposal-view {
  display: flex;
  flex-direction: column;

  &__footer {
    display: flex;

    &--filter {
      margin-left: auto;

      display: flex;
      padding: 20px;

      align-items: center;
      justify-content: center;

      p {
        $recuo: 30px;

        margin-right: $recuo * (-1);

        display: none;
        padding: 5px #{$recuo * 1.5} 5px #{$recuo};
        border-radius: 30px 0 0 30px;
        background: $color-primary;

        font-size: 22px;
        color: $color-light;
        text-align: center;

        pointer-events: none;

        @media (min-width: $breakpoint-desktop) {
          display: block;
        }
      }

      i {
        padding: 15px;
        border-radius: 50%;
        background: $color-secondary;
        box-shadow: 2px 2px 5px 0px rgba(#ABABAB, 0.75);

        font-size: 25px;
        color: $color-light;

        transition: all .3s ease-in-out;
        cursor: pointer;

        @media(max-width: $breakpoint-mobile){
          font-size: 18px;
        }

        &:hover {
          background: $color-primary;
        }
      }

      &.is-filtered {
        i {
          background: $color-primary;
          color: $color-light;
        }
      }
    }
  }
}

.sticky {
  position: sticky;
  top: 0;
  z-index: 1;

  background-color: #fff;
}

.ellipsis {
  display: block;
  max-width: 200px;
  overflow: hidden;

  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
